/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import { Button, InlineNotification, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useConsumerMyPageHeader } from 'bundles/composable-page-header-consumer/contexts/ConsumerMyPageHeaderContext';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';
import { clearProgramSwitcherSelection } from 'bundles/unified-common/hooks/useSyncProgramSwitcherSelection';
import {
  USER_EXPERIENCE_SWITCH_PARAM,
  getSavedUserExperience,
} from 'bundles/unified-common/utils/userExperienceRedirectUtils';

import _t from 'i18n!nls/account-settings';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  description: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
  notificationWrapper: css`
    margin-top: var(--cds-spacing-200);
  `,
  button: css`
    align-self: flex-start;

    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
};

const UserContextSwitcher = () => {
  const { naptimeOrganizationsWithProgramEnrollments } = useConsumerMyPageHeader();
  const { userExperience, updateUserExperience, isUpdating, error } = useUserExperienceContext();
  const track = useTracker();

  const orgs = naptimeOrganizationsWithProgramEnrollments ?? [];
  const savedExperience = getSavedUserExperience(userExperience);

  const org = orgs.length === 1 ? (orgs[0] ?? null) : null;
  const isCurrentlyOnOrg =
    org != null && savedExperience?.type === 'enterprise' && savedExperience?.orgSlug === org.slug;

  const handleSwitch = useCallback<() => Promise<void>>(async () => {
    if (!org) return;

    const previousHomepage = isCurrentlyOnOrg ? org.id : '';
    const previousType = isCurrentlyOnOrg ? 'third_party_organization' : 'consumer';
    const newType = isCurrentlyOnOrg ? 'consumer' : 'third_party_organization';
    const newHomepage = isCurrentlyOnOrg ? '' : org.id;

    track('switch_homepage', {
      previousHomepage,
      currentHomepage: newHomepage,
      currentHomepageType: newType,
      previousHomepageType: previousType,
    });

    try {
      if (isCurrentlyOnOrg) {
        await Promise.all([updateUserExperience({ selection: 'consumer' }), clearProgramSwitcherSelection()]);
        window.location.assign(`/?${USER_EXPERIENCE_SWITCH_PARAM}=1`);
      } else {
        await updateUserExperience({ selection: 'enterprise', organizationId: org.id });
        window.location.assign(`/organizations/${org.slug}?${USER_EXPERIENCE_SWITCH_PARAM}=1`);
      }
    } catch {
      // Error surfaced via context
    }
  }, [org, isCurrentlyOnOrg, updateUserExperience, track]);

  if (orgs.length !== 1 || !org) return null;

  const description = isCurrentlyOnOrg
    ? _t(
        "You're currently using #{orgName}. Switch to your personal account to access courses not included in your organization's plan.",
        { orgName: org.name }
      )
    : _t(
        "You're currently using your personal account. Switch to #{orgName} to access your organization's managed courses and benefits.",
        { orgName: org.name }
      );

  const buttonLabel = isCurrentlyOnOrg
    ? _t('Switch to personal account')
    : _t('Switch to #{orgName}', { orgName: org.name });

  return (
    <div data-testid="user-context-switcher-section">
      <div css={styles.container}>
        <div css={styles.description}>
          <Typography2 component="h2" variant="subtitleLarge" id="user-context-switcher-heading">
            {_t('Switch account')}
          </Typography2>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {description}
          </Typography2>
        </div>
        <Button variant="secondary" onClick={handleSwitch} loading={isUpdating} css={styles.button}>
          {buttonLabel}
        </Button>
      </div>
      <div role="alert" aria-atomic="true" css={error ? styles.notificationWrapper : undefined}>
        {error && (
          <InlineNotification severity="error">{_t('Something went wrong. Please try again.')}</InlineNotification>
        )}
      </div>
    </div>
  );
};

export default UserContextSwitcher;
